import {AfterViewChecked, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup} from '@angular/forms';
import {MatStep} from '@angular/material/stepper';
import {ActivatedRoute, Router} from '@angular/router';
import {Observable, Observer, of, Subject} from 'rxjs';
import {switchMap, take, takeUntil} from 'rxjs/operators';
import {CaseStatus, CaseType} from '../../../types';
import {CaseHospitalDTO} from '../../../models/case-hospital/CaseHospitalDTO';
import {CaseMedicalService} from '../../../services/case-medical.service';
import {LookupValue} from '../../../types/lookup-value.enum';
import {SharedService} from '../../../../common/services/shared-service';
import {CaseAssociationStepComponent} from '../case-shared-steps/case-association-step/case-association-step.component';
import {CaseDetailsStepComponent} from '../case-shared-steps/medical/case-details-step/case-details-step.component';
import {CaseAdmissionStepComponent} from '../case-shared-steps/medical/case-admission-step/case-admission-step.component';
import {CaseDocumentationStepComponent} from '../case-shared-steps/medical/case-documentation-step/case-documentation-step.component';
import {CaseProvidersStepComponent} from '../case-shared-steps/medical/case-providers-step/case-providers-step.component';
import {CaseTransactionStepComponent} from '../case-shared-steps/case-transaction-step/case-transaction-step.component';
import {CaseUtilisatonStepComponent} from '../case-shared-steps/medical/case-utilisaton-step/case-utilisaton-step.component';
import {CaseIssuesStepComponent} from '../case-shared-steps/case-issues-step/case-issues-step.component';
import {CaseStatusStepComponent} from '../case-shared-steps/case-status-step/case-status-step.component';
import {CaseStatusLinkDTO} from '../../../models/CaseStatusLinkDTO';
import {CaseStatusDTO} from '../../../models/CaseStatusDTO';
import {DischargeType} from '../../dialogs/case-discharge-dialog/case-discharge-dialog.component';
import {CaseManagerLinkDTO} from '../../../models/CaseManagerLinkDTO';
import {ContactDTO} from '../../../../ent/models/ContactDTO';
import {CaseDTO} from '../../../models/CaseDTO';
import {CaseService} from '../../../services/case.service';
import {ProcessingClassSubType, ProcessingClassSubTypeDBMapping} from '../../../types/types';
import {NameValueDTO} from '../../../../common/models/NameValueDTO';
import {UserAuthorisationService} from 'src/app/modules/common/services/user-authorisation.service';
import {DisplayStrings} from 'src/app/modules/common/constants/DisplayStrings';
import {CaseHospitalFileLinkDTO} from '../../../models/case-hospital/CaseHospitalFileLinkDTO';


enum RouteHeadings {
  AddCase = 'Add Case',
  AddPharmacy = 'Add Pharmacy',
  EditCase = 'Edit Case',
  EditPharmacy = 'Edit Pharmacy',
}

enum RoutePaths {
  AddHospital = 'add-hospital-case',
  AddPharmacy = 'add-pharmacy-case',
  EditHospital = 'edit-hospital-case',
  EditPharmacy = 'edit-pharmacy-case',
}

enum FormGroupName {
  ASSOCIATION_FORM_GROUP = 'associationFormGroup',
  DETAILS_FORM_GROUP = 'detailsFormGroup',
  ADMISSION_FORM_GROUP = 'admissionFormGroup',
  DOCUMENTATION_FORM_GROUP = 'documentationFormGroup',
  PROVIDER_FORM_GROUP = 'providerFormGroup',
  TRANSACTION_FORM_GROUP = 'transactionFormGroup',
  UTILISATION_FORM_GROUP = 'utilisationFormGroup',
  ISSUE_TICKET_FORM_GROUP = 'issueTicketFormGroup',
  STATUS_FORM_GROUP = 'statusFormGroup',
}

@Component({
  selector: 'app-case-medical',
  templateUrl: './case-medical.component.html',
  styleUrls: [
    '../../../../../../styles/global/_add-view-edit-styles.scss',
  ]
})
export class CaseMedicalComponent implements OnInit, OnDestroy, AfterViewChecked {
  readonly destroyed$ = new Subject();
  readonly caseType = CaseType;
  readonly routeHeadings = RouteHeadings;
  readonly routePaths = RoutePaths;
  readonly lookupValue = LookupValue;
  readonly formGroupName = FormGroupName;
  readonly processingClassSubTypeEnum = ProcessingClassSubType;

  heading: string;
  backPageName: string;
  isEditMode: boolean;

  caseId: number;
  caseHospitalDTO: CaseHospitalDTO;

  associationFormGroup: FormGroup;
  detailsFormGroup: FormGroup;
  admissionFormGroup: FormGroup;
  documentationFormGroup: FormGroup;
  providersFormGroup: FormGroup;
  transactionFormGroup: FormGroup;
  utilisationFormGroup: FormGroup;
  paymentFormGroup: FormGroup;
  issueTicketFormGroup: FormGroup;
  statusFormGroup: FormGroup;

  // ---------------------------------
  // Specific functionality properties:
  // ---------------------------------
  icdCategoryInAssociationStep: string;
  isDetailsFormValid: boolean;
  isAdmissionFormValid: boolean;
  isProvidersFormValid: boolean;

  @Input() processingClassSubtype!: ProcessingClassSubType;

  @ViewChild('associationStep', {static: false}) private associationStep: CaseAssociationStepComponent;
  @ViewChild('detailsStep', {static: false}) private detailsStep: CaseDetailsStepComponent;
  @ViewChild('admissionStep', {static: false}) private admissionStep: CaseAdmissionStepComponent;
  @ViewChild('documentationStep', {static: false}) private documentationStep: CaseDocumentationStepComponent;
  @ViewChild('providersStep', {static: false}) private providersStep: CaseProvidersStepComponent;
  @ViewChild('transactionStep', {static: false}) private transactionStep: CaseTransactionStepComponent;
  @ViewChild('utilisationStep', {static: false}) private utilisationStep: CaseUtilisatonStepComponent;
  @ViewChild('paymentStep', {static: false}) private paymentStep: CaseUtilisatonStepComponent;
  @ViewChild('issueStep', {static: false}) private issueStep: CaseIssuesStepComponent;
  @ViewChild('statusStep', {static: false}) private statusStep: CaseStatusStepComponent;

  // ViewChild for Mat Steps for a Stepper:
  @ViewChild('associationMatStep', {static: false}) private associationMatStep: MatStep;
  @ViewChild('detailsMatStep', {static: false}) private detailsMatStep: MatStep;
  @ViewChild('admissionMatStep', {static: false}) private admissionMatStep: MatStep;
  @ViewChild('documentationMatStep', {static: false}) private documentationMatStep: MatStep;
  @ViewChild('providersMatStep', {static: false}) private providersMatStep: MatStep;
  @ViewChild('transactionMatStep', {static: false}) private transactionMatStep: MatStep;
  @ViewChild('utilisationMatStep', {static: false}) private utilisationMatStep: MatStep;
  @ViewChild('paymentMatStep', {static: false}) private paymentMatStep: MatStep;
  @ViewChild('issuesMatStep', {static: false}) private issuesMatStep: MatStep;
  @ViewChild('statusMatStep', {static: false}) private statusMatStep: MatStep;

  private associationPayload: any;
  private detailsPayload: any;
  private admissionPayload: any;
  private documentationPayload: any;
  private providerPayload: any;
  private transactionPayload: any;
  private issueTicketsPayload: any;
  private statusPayload: any;
  private loggedInUserName: string;

  constructor(private changeDetect: ChangeDetectorRef,
              private caseService: CaseService,
              private caseMedicalService: CaseMedicalService,
              private formBuilder: FormBuilder,
              private route: ActivatedRoute,
              private router: Router,
              private sharedService: SharedService,
              private userAuthorisationService: UserAuthorisationService
  ) {
    this.backPageName = 'claim-management';
    this.setHeadingsAndEditMode();
  }

  get isAuthorised(): boolean {
    return this.userAuthorisationService.hasRoleClaimsAssessor();
  }

  get bannerText(): string {
    return DisplayStrings.permissionsErrorMessages.cannotEditCase;
  }

  setHeadingsAndEditMode() {
    if (this.route.snapshot.url[1].path === this.routePaths.EditHospital) {
      this.heading = this.routeHeadings.EditCase;
      this.isEditMode = true;
    }
    else if (this.route.snapshot.url[1].path === this.routePaths.EditPharmacy) {
      this.heading = this.routeHeadings.EditPharmacy;
      this.isEditMode = true;
    }
    else if (this.route.snapshot.url[1].path === this.routePaths.AddHospital) {
      this.heading = this.routeHeadings.AddCase;
      this.isEditMode = false;
    }
    else if (this.route.snapshot.url[1].path === this.routePaths.AddPharmacy) {
      this.heading = this.routeHeadings.AddPharmacy;
      this.isEditMode = false;
    }
  }

  ngOnInit(): void {
    this.initSubscriptions();
    this.initStepFormGroup();

    if (this.isEditMode) {
      this.getCaseHospitalByCaseId(this.caseId);
    }
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  ngAfterViewChecked(): void {
    // Note: Setting of Heading from Child and Passing Back to Parent
    // Resetting on "onUpdateHeadingChanged" resulted into "ExpressionChangedAfterItHasBeenCheckedError"
    this.changeDetect.detectChanges();
  }

  initSubscriptions(): void {
    // Route Subscription
    this.route.params.subscribe(params => {
      this.caseId = params.id;
    });

    this.sharedService.getLoggedInProfile$()
      .pipe(
        take(1)
      )
      .subscribe(() => {
        // Note: Won't be using profile obj instead invoke `getLoggedInUserName` as username that is already set in service.
        this.loggedInUserName = this.sharedService.getLoggedInUserName();
      });
  }

  initStepFormGroup(): void {
    this.associationFormGroup = this.formBuilder.group({});
    this.detailsFormGroup = this.formBuilder.group({});
    this.admissionFormGroup = this.formBuilder.group({});
    this.documentationFormGroup = this.formBuilder.group({});
    this.providersFormGroup = this.formBuilder.group({});
    this.transactionFormGroup = this.formBuilder.group({});
    this.utilisationFormGroup = this.formBuilder.group({});
    this.paymentFormGroup = this.formBuilder.group({});
    this.issueTicketFormGroup = this.formBuilder.group({});
    this.statusFormGroup = this.formBuilder.group({});
  }

  onUpdateHeadingChanged(value: string): void {
    this.heading = value;
  }

  onUpdateICDCategoryChangedInAssociationStep(value: string): void {
    this.icdCategoryInAssociationStep = value;
  }

  onUpdateStepForm($event: any, formGroupName: FormGroupName): void {
    if (this.formGroupName.ASSOCIATION_FORM_GROUP === formGroupName) {
      this.associationFormGroup = $event;
    }
    else if (this.formGroupName.DETAILS_FORM_GROUP === formGroupName) {
      this.detailsFormGroup = $event;
    }
    else if (this.formGroupName.ADMISSION_FORM_GROUP === formGroupName) {
      this.admissionFormGroup = $event;
    }
    else if (this.formGroupName.TRANSACTION_FORM_GROUP === formGroupName) {
      this.transactionFormGroup = $event;
    }
    else if (this.formGroupName.UTILISATION_FORM_GROUP === formGroupName) {
      this.utilisationFormGroup = $event;
    }
    else if (this.formGroupName.ISSUE_TICKET_FORM_GROUP === formGroupName) {
      this.issueTicketFormGroup = $event;
    }
    else if (this.formGroupName.STATUS_FORM_GROUP === formGroupName) {
      this.statusFormGroup = $event;
    }
  }

  handleCommunicationNumberValue(personId: number): void {
    if (personId) {
      this.caseService.getContactByInsuredPerson(personId)
        .pipe(
          takeUntil(this.destroyed$),
        )
        .subscribe((response: ContactDTO) => {
            if (response) {
              if (!this.isEditMode) {
                this.detailsStep.communicationNumberAbstractControl.patchValue(response.mobileNumber);
              }
              else {
                const caseDTO: CaseDTO = this.caseHospitalDTO.caseDTO;
                if (!caseDTO.communicationNumber) {
                  this.detailsStep.communicationNumberAbstractControl.patchValue(response.mobileNumber);
                }
                else {
                  this.detailsStep.communicationNumberAbstractControl.patchValue(caseDTO.communicationNumber);
                }
              }
            }
          },
          (errorResponse) => {
            const error = errorResponse.error;
            this.sharedService.openErrorDialog(error.businessMessage);
          });
    }
  }

  setPayloadFromEachStep(): void {
    this.associationPayload = this.associationStep.getPayload();
    this.detailsPayload = this.detailsStep.getPayload();
    this.admissionPayload = this.admissionStep.getPayload();
    this.documentationPayload = this.documentationStep.getPayload();
    this.providerPayload = this.providersStep.getPayload();

    // Step is visible on "Edit Mode" only
    if (this.isEditMode) {
      this.transactionPayload = this.transactionStep.getPayload();
    }

    this.issueTicketsPayload = this.issueStep.getPayload();
    this.statusPayload = this.statusStep.getPayload();
  }

  saveCaseHospitalDTOAndCreateLinkedCase(dischargeType: string): void {
    // ----------------------------
    // Important: Get Updated Payload from each step BEFORE creating Final Payload
    this.setPayloadFromEachStep();

    // ----------------------------
    // Note:
    // Case-1: Final Discharge option save payload as usual.
    // Case-2: Save payload as usual + Open a new Linked Case:
    // ----------------------------

    const caseHospitalDTOOriginalPayload = this.getCaseHospitalDTOPayload();
    const {caseStatuses, caseManagers} = caseHospitalDTOOriginalPayload;
    const latestStatus: CaseStatusLinkDTO = caseStatuses[0];

    /*
     Case Open => Close Original Payload in Both Case
     - Case-1: Other Options
     - Case-2: Option => Final Discharge
     */

    if (latestStatus.caseStatusDTO.statusName === CaseStatus.Open) {
      const caseStatusLinkDTO = new CaseStatusLinkDTO();
      const caseStatusDTO = new CaseStatusDTO();
      caseStatusDTO.caseStatusId = 2; // Closed Value: 2
      caseStatusDTO.statusName = CaseStatus.Closed;
      caseStatusLinkDTO.caseStatusDTO = caseStatusDTO;
      caseStatusLinkDTO.startDate = new Date();
      // Close => Original payload
      caseHospitalDTOOriginalPayload.caseStatuses = [caseStatusLinkDTO, ...caseStatuses];
    }

    if (dischargeType === DischargeType.FinalDischarge) {
      this.saveCaseHospitalDTO$(caseHospitalDTOOriginalPayload)
        .subscribe(
          (isSaved) => {
            if (isSaved) {
              this.sharedService.openSnackBar(`Case ${this.isEditMode ? 'edited' : 'added'} successfully`, 'Dismiss');
              this.router.navigate(['/claim-management'], {queryParams: {tab: 'cases'}});
            }
          });
    }
    else {
      this.saveCaseHospitalDTO$(caseHospitalDTOOriginalPayload)
        .pipe(
          switchMap((isSaved: boolean): Observable<boolean | CaseHospitalDTO> => {
            if (isSaved) {
              const caseHospitalDTODischargePayloadLinked: CaseHospitalDTO = this.getNewLinkedCaseDischargeDTOPayload(dischargeType);
              const latestManager: CaseManagerLinkDTO = caseManagers[0];
              latestManager.startDate = new Date();
              caseHospitalDTODischargePayloadLinked.caseManagers = [latestManager];
              return this.saveCaseHospitalDTO$(caseHospitalDTODischargePayloadLinked);
            }
            else {
              return of(false);
            }
          }))
        .subscribe({
          next: (isNewCaseLinked: boolean | CaseHospitalDTO) => {
            if (isNewCaseLinked) {
              this.sharedService.openSnackBar(`Case discharge case created successfully`, 'Dismiss');
              this.router.navigate(['/claim-management'], {queryParams: {tab: 'cases'}});
            }
          },
          error: (errorResponse) => {
            const error = errorResponse.error;
            this.sharedService.openErrorDialog(error.businessMessage);
          }
        });
    }
  }

  saveCaseHospitalDTO(): void {
    // ----------------------------
    // Case: Simple Save without opening a dialog
    // Important: Get Updated Payload from each step BEFORE creating Final Payload
    this.setPayloadFromEachStep();
    // ----------------------------

    const caseHospitalDTOPayload = this.getCaseHospitalDTOPayload();
    this.saveCaseHospitalDTO$(caseHospitalDTOPayload)
      .subscribe(
        (isSaved) => {
          if (isSaved) {
            this.sharedService.openSnackBar(`Case ${this.isEditMode ? 'edited' : 'added'} successfully`, 'Dismiss');
            this.router.navigate(['/claim-management'], {queryParams: {tab: 'cases'}});
          }
        });
  }

  getLabelByCaseMedicalSubType(subtype: string): string {
    if (subtype === ProcessingClassSubType.Pharmacy) {
      return 'Conditions';
    }
    else {
      return 'Reasons for Admission';
    }
  }

  handleDeleteNewDocumentWithoutCaseSave(deleteDocumentDetails: {
    caseHospitalId: number | bigint,
    fileId: bigint
  }): void {
    const {caseHospitalId, fileId} = deleteDocumentDetails;
    if (!caseHospitalId && fileId) {
      return;
    }
    this.caseMedicalService.deleteDocument(caseHospitalId, fileId)
      .pipe(take(1))
      .subscribe({
        next: (_: CaseHospitalFileLinkDTO) => {
          // Do Nothing
        },
        error: (errorResponse) => {
          const error = errorResponse.error || 'Failed to delete document';
          console.error(error);
        }
      });
  }

  handleAddNewDocumentWithoutCaseSave(documentPayload: CaseHospitalFileLinkDTO): void {
    if (!documentPayload) {
      return;
    }
    this.caseMedicalService.createDocument(documentPayload)
      .pipe(take(1))
      .subscribe({
        next: (_: CaseHospitalFileLinkDTO) => {
          // Do Nothing
        },
        error: (errorResponse) => {
          const error = errorResponse.error || 'Failed to save document';
          console.error(error);
        }
      });
  }

  private getNewLinkedCaseDischargeDTOPayload(dischargeType: string): CaseHospitalDTO {
    let caseHospitalDTOPayloadLinked = this.getCaseHospitalDTOPayload();
    const caseName = caseHospitalDTOPayloadLinked.caseDTO.name;
    caseHospitalDTOPayloadLinked = {
      ...caseHospitalDTOPayloadLinked,
      caseHospitalId: null,
      caseDTO: {
        ...caseHospitalDTOPayloadLinked.caseDTO,
        caseId: null,
        name: dischargeType === DischargeType.FinalDischarge ? `Discharge: ${caseName}` : `Discharge ${this.getAppendText(dischargeType)}: ${caseName}`
      },
      caseStatuses: [this.getCaseStatusLinkDTOPayloadForLinkedCase()],
      encounterCases: null,
      caseHospitalFiles: null
    };
    return caseHospitalDTOPayloadLinked;
  }

  private getAppendText(dischargeType: string): string {
    if (dischargeType === DischargeType.TransferToSubAcute) {
      return ' (Transfer to sub-acute)';
    }
    else if (dischargeType === DischargeType.ContinuanceOfCare) {
      return ' (Continuance of care)';
    }
    else if (dischargeType === DischargeType.PrivateHomeNursing) {
      return ' (Private Home Nursing)';
    }
  }

  private getCaseStatusLinkDTOPayloadForLinkedCase(): CaseStatusLinkDTO {
    const caseStatusLinkDTO: CaseStatusLinkDTO = new CaseStatusLinkDTO();
    const caseStatusDTO: CaseStatusDTO = new CaseStatusDTO();

    caseStatusDTO.caseStatusId = 1;
    caseStatusDTO.statusName = 'open';
    caseStatusDTO.created = new Date();
    caseStatusDTO.createdBy = this.loggedInUserName;
    caseStatusDTO.updated = this.isEditMode ? new Date() : null;
    caseStatusDTO.updatedBy = this.isEditMode ? this.loggedInUserName : null;
    caseStatusDTO.tenantId = null;

    caseStatusLinkDTO.created = new Date();
    caseStatusLinkDTO.createdBy = this.loggedInUserName;
    caseStatusLinkDTO.updated = this.isEditMode ? new Date() : null;
    caseStatusLinkDTO.updatedBy = this.isEditMode ? this.loggedInUserName : null;
    caseStatusLinkDTO.tenantId = null;

    caseStatusLinkDTO.caseStatusDTO = caseStatusDTO;
    caseStatusLinkDTO.startDate = new Date();
    caseStatusLinkDTO.endDate = null;

    return caseStatusLinkDTO;
  }

  private saveCaseHospitalDTO$(caseHospitalDTOPayload: CaseHospitalDTO): Observable<boolean> {
    return new Observable((observer: Observer<boolean>) => {
      this.caseMedicalService.saveCaseMedical(caseHospitalDTOPayload)
        .pipe(
          takeUntil(this.destroyed$),
        )
        .subscribe(
          (response: CaseHospitalDTO) => {
            if (response) {
              observer.next(true);
            }
            else {
              observer.next(false);
            }
          },
          (errorResponse) => {
            observer.next(false);
            const error = errorResponse.error;
            this.sharedService.openErrorDialog(error.businessMessage);
          }
        );
    });
  }

  private getCaseHospitalDTOPayload(): CaseHospitalDTO {
    let payload: CaseHospitalDTO;
    payload = {
      caseHospitalId: this.isEditMode ? this.caseHospitalDTO.caseHospitalId : null,
      guardianNextOfKinMobile: this.isEditMode ? this.caseHospitalDTO.guardianNextOfKinMobile : null,
      workmanCompensationCaseInd: this.isEditMode ? this.caseHospitalDTO.workmanCompensationCaseInd : null,
      workmansCompensationCaseNumber: this.detailsPayload.workmansCompensationCaseNumber,
      roadAccidentFundCaseInd: this.isEditMode ? this.caseHospitalDTO.roadAccidentFundCaseInd : null,
      roadAccidentFundCaseNumber: this.detailsPayload.roadAccidentFundCaseNumber,
      transportType: this.detailsPayload.transportType,
      reasonForAdmission: this.detailsPayload.reasonForAdmission,
      caseDTO: {
        ...this.associationPayload.caseDTO,
        icdCategoryName: this.admissionPayload.icdCategory, // This will be taken from Primary Diagnosis in Hospital Case
        communicationNumber: this.detailsPayload.communicationNumber,
        insuredPerson: this.associationPayload.insuredPerson,
        policy: this.associationPayload.policy,
        product: this.associationPayload.product,
        processingSubClass: this.getProcessingSubClassByMedicalSubType(this.processingClassSubtype)
      },
      caseStatuses: this.statusPayload.caseStatuses,
      caseManagers: this.statusPayload.caseManagers,
      encounterCases: this.isEditMode ? this.transactionPayload.encounterCases : null, // Step is visible on "Edit Mode" only
      issueTicketCases: this.issueTicketsPayload.issueTicketCases,
      caseHospitalFiles: this.documentationPayload.caseHospitalFiles,
      caseHospitalDiagnoses: this.admissionPayload.caseHospitalDiagnoses,
      caseHospitalCodingSchemas: this.admissionPayload.caseHospitalCodingSchemas,
      caseHospitalProviders: this.providerPayload.caseHospitalProviders,
      created: this.isEditMode ? this.caseHospitalDTO.created : new Date(),
      createdBy: this.isEditMode ? this.caseHospitalDTO.createdBy : this.loggedInUserName,
      updated: this.isEditMode ? new Date() : null,
      updatedBy: this.isEditMode ? this.loggedInUserName : null,
      tenantId: null
    } as CaseHospitalDTO;
    return payload;
  }

  private getProcessingSubClassByMedicalSubType(subtype: ProcessingClassSubType): NameValueDTO {
    const processingSubClass: NameValueDTO = new NameValueDTO();
    processingSubClass.name = ProcessingClassSubType[subtype];
    processingSubClass.value = ProcessingClassSubTypeDBMapping[subtype];
    return processingSubClass;
  }

  private getCaseHospitalByCaseId(caseId: number): void {
    this.caseMedicalService.getCaseMedicalBy(caseId)
      .pipe(
        take(1),
      )
      .subscribe(
        (response: CaseHospitalDTO) => {
          if (response) {
            this.caseHospitalDTO = response;
          }
          else {
            const message = `Case ${caseId} data not found`;
            this.sharedService.openSnackBar(message, 'Dismiss');
          }
        },
        (errorResponse) => {
          const error = errorResponse.error;
          this.sharedService.openErrorDialog(error.businessMessage);
        }
      );
  }
}
